import { PublicClientApplication, type Configuration } from '@azure/msal-browser';

// MSAL configuration for the WPI CRM portal. Pattern copied from
// wise-iam/portal/src/auth/msalConfig.ts (the platform's MSAL v5 reference).
//
// App registration: "WISE REMS CRM {env}" Entra app registrations — created
// out-of-band before first deploy. Each needs:
//   - SPA platform with redirect URI
//     https://app-rems-crm-portal-{env}.azurewebsites.net/auth/callback
//     + http://localhost:5173/auth/callback (Vite dev server)
//   - An exposed API scope (VITE_AZURE_API_SCOPE)
//   - Delegated Graph perms User.Read + openid + profile + email
//   - api.requestedAccessTokenVersion = 2
//
// VITE_* values are baked at BUILD time — the pipeline builds one portal
// artifact per environment with that env's values (and guards against
// baking a non-prod clientId into the prod artifact).

const CLIENT_ID = import.meta.env['VITE_AZURE_CLIENT_ID'] as string;
const TENANT_ID = import.meta.env['VITE_AZURE_TENANT_ID'] as string;
const API_SCOPE = import.meta.env['VITE_AZURE_API_SCOPE'] as string;

if (!CLIENT_ID || !TENANT_ID || !API_SCOPE) {
  throw new Error(
    'Missing MSAL env vars — set VITE_AZURE_CLIENT_ID, VITE_AZURE_TENANT_ID, VITE_AZURE_API_SCOPE in portal/.env',
  );
}

export const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : '',
    postLogoutRedirectUri: typeof window !== 'undefined' ? window.location.origin : '',
  },
  cache: {
    // localStorage, not sessionStorage (the platform rule, Chauncey White
    // 2026-10-23: every link lands signed in). A link opens a NEW tab, and
    // sessionStorage belongs to one tab — so every link used to arrive with
    // nobody signed in and go round Entra again. With localStorage the new tab
    // finds the account and its refresh token and never redirects at all. A
    // shared browser is covered by the link's ?login_hint beating whoever is
    // cached (auth/ssoMemory.ts linkAccount); Sign out clears this cache.
    cacheLocation: 'localStorage',
  },
  system: {
    // MSAL v5 silent renewal: the parent frame waits on this timeout for a
    // BroadcastChannel message from the hidden iframe. The message only
    // arrives if the page loaded at redirectUri inside the iframe calls
    // broadcastResponseToMainFrame() — which we wire up in main.tsx. Without
    // that bridge NO timeout value helps (wise-iam chased 10s→20s bumps
    // before the 2026-05-29 root-cause fix in its main.tsx). 10s is ample
    // headroom for the bridge handshake even under cold cache.
    // See https://aka.ms/msaljs/browser-errors#timed_out
    iframeBridgeTimeout: 10000,
  },
};

// What we ask for at interactive login. Includes the custom API scope so
// the portal can immediately call its own backend without a second prompt.
export const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'offline_access', API_SCOPE],
};

// Silent token request — used by the (future) api client on every fetch to
// acquire a fresh access token. Falls back to acquireTokenRedirect on
// InteractionRequired.
export const tokenRequest = {
  scopes: [API_SCOPE],
};

export const msalInstance = new PublicClientApplication(msalConfig);
